/**
 * Detect openable / copyable values (web links, local paths, bucket URIs)
 * for the app-wide hover tip.
 */

import { joinPathLineBreaks } from './variableScope/resolve';

const WEB_URL_RE = /^(https?:\/\/|www\.)/i;
const WIN_DRIVE_RE = /^[A-Za-z]:[\\/]/;
const WIN_UNC_RE = /^\\\\[^\\/\s]+[\\/]/;
const UNIX_ABS_RE = /^\/(?:Users|home|Volumes|mnt|opt|var|tmp|private|Applications)\//;
const FILE_EXT_RE = /\.[A-Za-z0-9]{1,12}$/;

const BUCKET_SCHEME_RE =
  /^(?:s3|s3a|s3n|gs|b2|r2|oss|cos|az|abfs|abfss|wasb|wasbs|minio|ibmcos|oci|swift):\/\//i;
const GENERIC_URI_SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;
const OPENABLE_URI_SCHEME_RE = /^(?:https?|file):\/\//i;

/** Matches openable tokens inside a plain text node. */
export const OPENABLE_IN_TEXT_RE =
  /(?:https?:\/\/|www\.)[^\s<>"'()]+|(?:s3|s3a|s3n|gs|b2|r2|oss|cos|az|abfs|abfss|wasb|wasbs|minio|ibmcos|oci|swift):\/\/[^\s<>"']+|[a-z][a-z0-9+.-]*:\/\/[^\s<>"']+|[A-Za-z]:(?:\\|\/)[^\s<>"']+|\\\\[^\s<>"']+|\/(?:Users|home|Volumes|mnt|opt|var|tmp|private|Applications)\/[^\s<>"']+/gi;

export const fileUrlToPath = (raw) => {
  let s = String(raw || '').trim();
  if (!/^file:/i.test(s)) return s;
  try {
    s = decodeURIComponent(s);
  } catch {
    /* keep undecoded */
  }
  s = s.replace(/^file:\/\//i, '');
  if (/^\/[A-Za-z]:/.test(s)) s = s.slice(1);
  return s;
};

const looksLikeLocalPath = (value) => {
  if (!value || /\n|\r/.test(value)) return false;
  if (WIN_DRIVE_RE.test(value) || WIN_UNC_RE.test(value)) return true;
  if (UNIX_ABS_RE.test(value)) return true;
  if (/^\/[^/\s]+\/[^/\s]/.test(value)) return true;
  return false;
};

const isLikelyFile = (localPath) => {
  if (/[\\/]$/.test(localPath)) return false;
  const base = localPath.split(/[\\/]/).pop() || '';
  if (!base || base === '.' || base === '..') return false;
  return FILE_EXT_RE.test(base) && !base.startsWith('.');
};

const isBucketUri = (value) => {
  if (BUCKET_SCHEME_RE.test(value)) return true;
  return GENERIC_URI_SCHEME_RE.test(value) && !OPENABLE_URI_SCHEME_RE.test(value);
};

/**
 * @returns {{ kind: 'url'|'local-folder'|'local-file'|'bucket'|'text', target: string }}
 */
export const classifyOpenableValue = (raw) => {
  const value = joinPathLineBreaks(String(raw ?? '').trim(), { joinInternal: true });
  if (!value) return { kind: 'text', target: '' };

  if (isBucketUri(value)) {
    return { kind: 'bucket', target: value };
  }

  if (WEB_URL_RE.test(value)) {
    const target = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return { kind: 'url', target };
  }

  if (/^file:/i.test(value)) {
    const localPath = fileUrlToPath(value);
    if (looksLikeLocalPath(localPath) || WIN_DRIVE_RE.test(localPath) || localPath.startsWith('/')) {
      return {
        kind: isLikelyFile(localPath) ? 'local-file' : 'local-folder',
        target: localPath,
      };
    }
  }

  if (looksLikeLocalPath(value)) {
    return {
      kind: isLikelyFile(value) ? 'local-file' : 'local-folder',
      target: value,
    };
  }

  return { kind: 'text', target: value };
};

/** @deprecated use classifyOpenableValue */
export const classifyVariableValue = classifyOpenableValue;

export const canOpenLocalPaths = () =>
  typeof window !== 'undefined' && typeof window.electronAPI?.openPath === 'function';

export const openableValueHint = (kind, { canOpenLocal = canOpenLocalPaths() } = {}) => {
  switch (kind) {
    case 'url':
      return 'This appears to be a web link, click to open';
    case 'local-folder':
      return canOpenLocal
        ? 'This appears to be a local folder, click to open'
        : 'This appears to be a local folder, click to copy';
    case 'local-file':
      return canOpenLocal
        ? 'This appears to be a local file, click to open'
        : 'This appears to be a local file, click to copy';
    case 'bucket':
      return 'This appears to be a bucket link, click to copy';
    default:
      return '(Click to Copy to Clipboard)';
  }
};

/** @deprecated use openableValueHint */
export const variableValueHint = openableValueHint;

export const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
};

const openViaAnchor = (href) => {
  const a = document.createElement('a');
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Perform the tip's click action.
 * @returns {Promise<'opened'|'copied'|'failed'>}
 */
export const openOpenableValue = async (kind, target) => {
  if (!target) return 'failed';

  if (kind === 'url') {
    if (typeof window.electronAPI?.openExternal === 'function') {
      try {
        const result = await window.electronAPI.openExternal(target);
        if (result?.ok) return 'opened';
      } catch {
        /* fall through to window.open */
      }
    }
    // Must stay synchronous relative to the user gesture when possible.
    // If we already awaited Electron above, <a> click is a safer fallback
    // than a popup-blocked window.open.
    try {
      const win = window.open(target, '_blank', 'noopener,noreferrer');
      if (!win) openViaAnchor(target);
    } catch {
      openViaAnchor(target);
    }
    return 'opened';
  }

  if (kind === 'local-folder' || kind === 'local-file') {
    if (typeof window.electronAPI?.openPath === 'function') {
      try {
        const result = await window.electronAPI.openPath(target);
        if (result?.ok) return 'opened';
        return 'failed';
      } catch {
        return 'failed';
      }
    }
    // Browser / no desktop bridge — cannot open local paths
    await copyTextToClipboard(target);
    return 'copied';
  }

  await copyTextToClipboard(target);
  return 'copied';
};

const trimTrailingPunctuation = (s) => s.replace(/[.,;:!?)\]}'"]+$/g, '');

const isInternalAppHref = (href) => {
  if (!href) return true;
  if (href.startsWith('#')) return true;
  if (href.startsWith('javascript:')) return true;
  // In-app router paths (not //cdn… protocol-relative)
  if (href.startsWith('/') && !href.startsWith('//')) return true;
  return false;
};

/**
 * Find an openable value under the pointer.
 * Returns null when nothing actionable is there.
 */
export const findOpenableAtPoint = (clientX, clientY) => {
  const el = document.elementFromPoint(clientX, clientY);
  if (!el) return null;
  if (el.closest?.('.openable-value-tip, .openable-value-menu, .doc-variable-tip')) return null;

  // 1) Variable pills — always tip (label = var metadata; action = display value)
  // Math pills edit in place. No open/copy target, so a click reaches the
  // formula menu instead of being swallowed as a clipboard action.
  const mathPill = el.closest?.('[data-doc-math]');
  if (mathPill) {
    const display = (mathPill.textContent || '').trim();
    const expr = (mathPill.getAttribute('data-math-expr') || '').trim();
    const color =
      mathPill.style.getPropertyValue('--var-color') ||
      getComputedStyle(mathPill).getPropertyValue('--var-color').trim() ||
      '';
    return {
      id: mathPill,
      label: expr || display,
      kind: 'math',
      target: '',
      color,
      state: mathPill.getAttribute('data-var-state') || '',
      source: 'math',
    };
  }

  const pill = el.closest?.('[data-doc-variable]');
  if (pill) {
    const name = pill.getAttribute('data-var-name') || '';
    const rawIndex = (pill.getAttribute('data-var-index') || '').trim();
    const ref =
      name && rawIndex === '*'
        ? `${name}[*]`
        : name && rawIndex && /^\d+$/.test(rawIndex) && parseInt(rawIndex, 10) >= 1
          ? `${name}[${parseInt(rawIndex, 10)}]`
          : name;
    const display = (pill.textContent || '').trim();
    const { kind, target } = classifyOpenableValue(display);
    const label =
      pill.getAttribute('data-var-tip') ||
      (ref ? `{{${ref}}} - No value in this context.` : display);
    if (!label && !display) return null;
    const color =
      pill.style.getPropertyValue('--var-color') ||
      getComputedStyle(pill).getPropertyValue('--var-color').trim() ||
      '';
    return {
      id: pill,
      label: label || display,
      kind,
      target: target || display,
      color,
      state: pill.getAttribute('data-var-state') || '',
      source: 'variable',
    };
  }

  // 2) Real anchors (external only)
  const anchor = el.closest?.('a[href]');
  if (anchor) {
    const href = (anchor.getAttribute('href') || '').trim();
    if (!isInternalAppHref(href)) {
      const { kind, target } = classifyOpenableValue(href);
      const actionKind = kind === 'text' ? 'url' : kind;
      const actionTarget =
        kind === 'text'
          ? /^https?:\/\//i.test(href)
            ? href
            : href.startsWith('//')
              ? `https:${href}`
              : href
          : target || href;
      return {
        id: anchor,
        label: href,
        kind: actionKind,
        target: actionTarget,
        color: '',
        state: '',
        source: 'anchor',
      };
    }
  }

  // 3) Plain text token (path / url / bucket) under the caret
  let node = null;
  let offset = 0;
  if (typeof document.caretRangeFromPoint === 'function') {
    const range = document.caretRangeFromPoint(clientX, clientY);
    if (range) {
      node = range.startContainer;
      offset = range.startOffset;
    }
  } else if (typeof document.caretPositionFromPoint === 'function') {
    const pos = document.caretPositionFromPoint(clientX, clientY);
    if (pos) {
      node = pos.offsetNode;
      offset = pos.offset;
    }
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;
  if (node.parentElement?.closest?.('[data-doc-variable], [data-doc-math], a[href], .openable-value-tip')) {
    return null;
  }

  const text = node.textContent || '';
  const re = new RegExp(OPENABLE_IN_TEXT_RE.source, 'gi');
  let match;
  while ((match = re.exec(text)) !== null) {
    const raw = trimTrailingPunctuation(match[0]);
    const start = match.index;
    const end = start + raw.length;
    if (offset < start || offset > end) continue;
    const { kind, target } = classifyOpenableValue(raw);
    if (kind === 'text') return null;
    return {
      id: `${node}-${start}-${end}-${raw}`,
      label: raw,
      kind,
      target: target || raw,
      color: '',
      state: '',
      source: 'text',
    };
  }

  return null;
};
